import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { getSetting, subscribe } from '@/services/userSettingsStore';
import {
  applyResolvedTheme,
  type DarkStyle,
  migrateLegacyDarkMode,
  persistDarkStyle,
  persistThemeSetting,
  prefersDarkSystemTheme,
  type ResolvedTheme,
  type ThemeSetting,
  toDarkStyle,
  toThemeSetting
} from '@/utils/theme';

const readTheme = () => toThemeSetting(getSetting('theme'));
const readDarkStyle = () => toDarkStyle(getSetting('darkStyle'));

export type { DarkStyle, ResolvedTheme, ThemeSetting };

export default function useThemeSetting() {
  // Folds the retired `darkMode` flag in before the first read below. In a
  // useState initializer so it runs once per mount and never from a snapshot.
  useState(() => {
    migrateLegacyDarkMode();
    return null;
  });

  // Subscribed to the settings store rather than held in local state, so the
  // theme follows the server copy when it lands (the case that matters right
  // after a cache clear) as well as local changes.
  const themeSetting = useSyncExternalStore(subscribe, readTheme, readTheme);
  const darkStyle = useSyncExternalStore(subscribe, readDarkStyle, readDarkStyle);

  const [systemPrefersDark, setSystemPrefersDark] = useState(prefersDarkSystemTheme);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setSystemPrefersDark(mediaQuery.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const resolvedTheme: ResolvedTheme =
    themeSetting === 'system' ? (systemPrefersDark ? 'dark' : 'light') : themeSetting;

  useEffect(() => {
    applyResolvedTheme(resolvedTheme, darkStyle);
  }, [darkStyle, resolvedTheme]);

  const setThemeSetting = useCallback((setting: ThemeSetting) => {
    persistThemeSetting(setting);
  }, []);

  const setDarkStyle = useCallback((style: DarkStyle) => {
    persistDarkStyle(style);
  }, []);

  return { themeSetting, setThemeSetting, resolvedTheme, darkStyle, setDarkStyle };
}
